import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { topicDetailPath } from "../../utils/routes";
import styles from "./Topics.module.css";
import { post } from "../../utils/request";
import {
  getFilePreviewUrls,
  getCachedPreviewUrl,
} from "../../utils/attachmentPreview";
import { t, noAutoTranslateUserTextProps, mergeReaderNameClass } from "../../utils/i18n";
import { normalizeModeratorsFromArticleData, createTopicModeratorChecker } from "../../utils/topicModerators";
import ModeratorVerifiedBadge from "../ModeratorVerifiedBadge/ModeratorVerifiedBadge";

const PAGE_SIZE = 10;

const pickCreatorDisplay = item => {
  const keys = [
    "creatorName",
    "createUserName",
    "nickName",
    "nickname",
    "username",
    "authorName",
  ];
  for (const k of keys) {
    const v = item?.[k];
    if (v == null || v === "") continue;
    if (typeof v === "string") {
      const s = v.trim();
      if (s) return s;
    }
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
  }
  const author = item?.author;
  if (typeof author === "string" && author.trim()) return author.trim();
  return "";
};

const Topics = ({ articleIds }) => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 从后端接口获取话题列表：POST /social/article/list
  useEffect(() => {
    let cancelled = false;

    const fetchTopics = async () => {
      setLoading(true);
      try {
        const res = await post("/social/article/list", {
          pageNo: 1,
          pageSize: PAGE_SIZE,
          sortingFieldList: [
            {
              field: "",
              order: "",
            },
          ],
        });

        const raw =
          (Array.isArray(res?.data?.data?.data) && res.data.data.data) ||
          (Array.isArray(res?.data?.data) && res.data.data) ||
          [];

        const list = raw.map(item => {
          const attachments = Array.isArray(item.attachments)
            ? item.attachments
            : [];

          // 统一转为字符串，与附件预览缓存 key 一致，便于列表与详情共用缓存
          const attachmentUuid =
            item.attachmentUuid != null && item.attachmentUuid !== ""
              ? String(item.attachmentUuid)
              : "";

          return {
            id: String(item.id),
            title: item.title || "",
            creatorDisplay: pickCreatorDisplay(item),
            creatorUserId:
              item.userId ??
              item.createUserId ??
              item.creatorId ??
              item.creatorUserId ??
              item.authorId ??
              "",
            moderators: normalizeModeratorsFromArticleData(item),
            content: item.content || "",
            games: Array.isArray(item.games) ? item.games : [],
            collections: Array.isArray(item.collections)
              ? item.collections
              : [],
            vote: item.vote || null,
            attachments,
            attachmentUuid,
            // 优先用缓存同步填封面，首屏已访问过的话题可立即显示图
            cover: getCachedPreviewUrl(attachmentUuid) || "",
          };
        });

        if (!cancelled) {
          setTopics(list);

          // 批量拉取封面（未命中缓存的会请求接口）
          // 去重：同一个 attachmentUuid 可能出现在多个话题里，避免重复请求预览接口
          const ids = list.map(t => t.attachmentUuid).filter(Boolean);
          const uniqueIds = Array.from(new Set(ids));
          if (uniqueIds.length === 0) return;

          getFilePreviewUrls(uniqueIds)
            .then(urls => {
              if (cancelled) return;
              const idToUrl = new Map();
              uniqueIds.forEach((id, i) => {
                if (urls[i]) idToUrl.set(id, urls[i]);
              });
              setTopics(prev =>
                prev.map(t => ({
                  ...t,
                  cover:
                    t.cover ||
                    (t.attachmentUuid
                      ? idToUrl.get(t.attachmentUuid) || ""
                      : ""),
                })),
              );
            })
            .catch(() => {});
        }
      } catch (err) {
        console.warn("获取话题列表失败:", err);
        if (!cancelled) {
          setTopics([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTopics();

    return () => {
      cancelled = true;
    };
  }, []);

  // 从附件中取封面图
  const getCoverImage = topic => {
    return (
      topic.cover ||
      (topic.attachments && topic.attachments[0]?.previewUrl) ||
      ""
    );
  };

  return (
    <div className={styles.topicsPage}>
      <h2 className={styles.title}>{t("topics.title", "Topics")}</h2>
      <p className={styles.subtitle}>
        {t("topics.subtitle", "Discover what players are discussing")}
      </p>

      {/* 话题卡片列表 */}
      {
        <div className={styles.grid}>
          {topics.map((topic, idx) => {
            const cover = getCoverImage(topic);
            return (
              <div
                key={topic.id}
                className={styles.card}
                onClick={() =>
                  navigate(topicDetailPath(topic.id))
                }
              >
                <div className={styles.iconWrapper}>
                  {cover ? (
                    <img
                      src={cover}
                      alt=""
                      className={styles.icon}
                      loading="eager"
                      decoding="async"
                      fetchPriority={idx < 6 ? "high" : "auto"}
                    />
                  ) : topic.attachmentUuid ? null : (
                    <div className={styles.iconPlaceholder}>
                      <span
                        className={[styles.iconPlaceholderText, "notranslate"]
                          .filter(Boolean)
                          .join(" ")}
                        {...noAutoTranslateUserTextProps}
                      >
                        {topic.title?.charAt(0) || "T"}
                      </span>
                    </div>
                  )}
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardTitle} title={topic.title}>
                    <span
                      className="notranslate"
                      {...noAutoTranslateUserTextProps}
                    >
                      {topic.title}
                    </span>
                  </div>
                  {topic.creatorDisplay ? (
                    <div className={styles.cardAuthor}>
                      <span className={styles.cardAuthorLine}>
                        <span
                          className={mergeReaderNameClass(true)}
                          {...noAutoTranslateUserTextProps}
                        >
                          {topic.creatorDisplay}
                        </span>
                        {createTopicModeratorChecker(topic.moderators)(
                          topic.creatorUserId,
                          topic.creatorDisplay,
                        ) ? (
                          <ModeratorVerifiedBadge />
                        ) : null}
                      </span>
                    </div>
                  ) : null}
                  {/* 关联游戏标签 */}
                  {topic.games && topic.games.length > 0 && (
                    <div className={styles.gameTags}>
                      {topic.games.slice(0, 2).map(g => (
                        <span key={g.id} className={styles.gameTag}>
                          <span
                            className="notranslate"
                            {...noAutoTranslateUserTextProps}
                          >
                            {g.name}
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* 投票参与人数（若有投票） */}
                  {topic.vote && (
                    <div className={styles.voteHint}>
                      🗳{" "}
                      {t("topics.voted", "{count} people voted", {
                        count: topic.vote.participantsCnt,
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* 无数据提示 */}
          {loading && topics.length === 0 && (
            <div className={styles.emptyWrap}>{t("common.loading", "Loading...")}</div>
          )}
          {!loading && topics.length === 0 && (
            <div className={styles.emptyWrap}>{t("topics.empty", "No topics")}</div>
          )}
        </div>
      }
    </div>
  );
};

export default Topics;
